import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'

type Theme = 'light' | 'dark'

const COLLAPSE_KEY = 'layout:sidebar-collapsed'
const THEME_KEY = 'layout:theme'
const THEME_CLASS = 'arco-theme-dark'
const DEFAULT_THEME: Theme = 'light'

const isBrowser = typeof window !== 'undefined'

const readCollapsedFromStorage = (): boolean | null => {
  if (!isBrowser) return null
  const stored = window.localStorage.getItem(COLLAPSE_KEY)
  if (stored === 'true') return true
  if (stored === 'false') return false
  return null
}

const readThemeFromStorage = (): Theme | null => {
  if (!isBrowser) return null
  const stored = window.localStorage.getItem(THEME_KEY) as Theme | null
  return stored === 'dark' || stored === 'light' ? stored : null
}

const prefersDarkMode = (): boolean => {
  if (!isBrowser || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

const applyThemeToDocument = (theme: Theme) => {
  if (!isBrowser) return
  document.body.setAttribute('data-theme', theme)
  document.documentElement.setAttribute('data-theme', theme)
  if (theme === 'dark') {
    document.body.classList.add(THEME_CLASS)
  } else {
    document.body.classList.remove(THEME_CLASS)
  }
}

export const useLayoutStore = defineStore('layout', () => {
  const initialized = ref(false)
  const isSidebarCollapsed = ref(false)
  const currentTheme = ref<Theme>(DEFAULT_THEME)

  const ensureHydrated = () => {
    if (initialized.value) return

    const storedCollapse = readCollapsedFromStorage()
    if (storedCollapse !== null) {
      isSidebarCollapsed.value = storedCollapse
    }

    const storedTheme = readThemeFromStorage()
    if (storedTheme) {
      currentTheme.value = storedTheme
    } else if (prefersDarkMode()) {
      currentTheme.value = 'dark'
    }

    initialized.value = true
  }

  const setSidebarCollapsed = (value: boolean) => {
    ensureHydrated()
    isSidebarCollapsed.value = value
  }

  const toggleSidebar = () => {
    setSidebarCollapsed(!isSidebarCollapsed.value)
  }

  const setTheme = (theme: Theme) => {
    ensureHydrated()
    currentTheme.value = theme
  }

  const toggleTheme = () => {
    setTheme(currentTheme.value === 'dark' ? 'light' : 'dark')
  }

  ensureHydrated()
  applyThemeToDocument(currentTheme.value)

  watch(
    isSidebarCollapsed,
    (value) => {
      if (!isBrowser) return
      window.localStorage.setItem(COLLAPSE_KEY, String(value))
    },
    { immediate: true }
  )

  watch(
    currentTheme,
    (theme) => {
      if (!isBrowser) return
      window.localStorage.setItem(THEME_KEY, theme)
      applyThemeToDocument(theme)
    },
    { immediate: true }
  )

  const isDarkTheme = computed(() => currentTheme.value === 'dark')

  return {
    isSidebarCollapsed,
    currentTheme,
    isDarkTheme,
    setSidebarCollapsed,
    toggleSidebar,
    setTheme,
    toggleTheme,
    ensureHydrated,
  }
})
